import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { colors, inr } from "@/theme";
import { Spinner, EmptyState } from "@/components/common";
import { Logo } from "@/components/Logo";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toast } from "sonner";

export default function PublicShortlist() {
  const { slug } = useParams();
  const [sl, setSl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(() => api(`/shortlists/share/${slug}`, { auth: false }).then(setSl).catch(() => setNotFound(true)), [slug]);
  useEffect(() => { load(); }, [load]);

  const markBought = async (pid) => {
    try { const u = await api(`/shortlists/share/${slug}/mark-bought`, { method: "POST", auth: false, body: { product_id: pid } }); setSl(u); toast.success("Marked as gifted 🎁"); }
    catch (e) { toast.error(e.message); }
  };

  if (notFound) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <PublicHeader />
      <EmptyState title="Table not found" message="This shared Nest Table may have been removed." action={<Link to="/" className="btn-primary" style={{ textDecoration: "none" }}>Visit Garlic</Link>} />
    </div>
  );
  if (!sl) return <Spinner full />;

  return (
    <div style={{ minHeight: "100vh", background: colors.surface }}>
      <PublicHeader />
      <div style={{ position: "relative", height: isMobile ? 220 : 320, overflow: "hidden", background: colors.surfaceSecondary }}>
        {sl.cover_image && <img src={sl.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(44,41,37,0.1), rgba(44,41,37,0.55))", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: isMobile ? 20 : 40 }}>
          <div className="eyebrow" style={{ color: "rgba(252,251,248,0.85)" }}>{sl.owner_name}'s Nest Table</div>
          <h1 className="font-display" style={{ color: "#FCFBF8", margin: "6px 0 0", fontSize: isMobile ? 32 : 46, fontWeight: 700 }}>{sl.name}</h1>
          {sl.message && <p className="font-body" style={{ color: "rgba(252,251,248,0.9)", marginTop: 6, maxWidth: 500 }}>{sl.message}</p>}
        </div>
      </div>

      <div className="container-max" style={{ padding: isMobile ? "24px 16px 48px" : "40px 24px 64px" }}>
        {sl.products.length === 0 ? (
          <EmptyState title="This table is empty" />
        ) : (
          <div className="product-grid">
            {sl.products.map((p) => (
              <div key={p.id} style={{ opacity: p.bought ? 0.6 : 1 }}>
                <Link to={`/product/${p.id}`} style={{ textDecoration: "none", color: colors.onSurface }}>
                  <div style={{ position: "relative", aspectRatio: "3 / 4", overflow: "hidden", border: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
                    {p.images?.[0] && <img src={p.images[0]} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                    {p.bought && <div style={{ position: "absolute", inset: 0, background: "rgba(74,95,69,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ background: colors.brand, color: "#fff", padding: "6px 14px", borderRadius: 999, fontWeight: 700, fontSize: 12 }}>GIFTED</span></div>}
                  </div>
                  <div style={{ padding: "10px 2px" }}>
                    <div className="font-body clamp-1" style={{ fontWeight: 500 }}>{p.name}</div>
                    <div className="font-body" style={{ fontWeight: 600, marginTop: 3 }}>{inr(p.price)}</div>
                  </div>
                </Link>
                {!p.bought && (
                  <button data-testid={`mark-bought-${p.id}`} onClick={() => markBought(p.id)} className="btn-outline" style={{ width: "100%", marginTop: 8, padding: 10 }}>
                    <Check size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />I'll gift this
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PublicHeader() {
  return (
    <header style={{ borderBottom: `1px solid ${colors.border}`, padding: "14px 24px", display: "flex", justifyContent: "center", background: colors.surface }}>
      <Logo size={22} />
    </header>
  );
}
